const LINE_ROOT_SELECTOR = '.DsPreviewYaml-Line-root';
const LINE_NUMBER_SELECTOR = '.DsPreviewYaml-Line-Index-LineNumber';
const LINE_CONTENT_SELECTOR = '.DsPreviewYaml-Line-Content';
const LINE_INDENT_SELECTOR = '.DsPreviewYaml-Indentation';
const SCROLLER_SELECTORS = ['.os-viewport', '.DsPreviewYaml-Scroller'] as const;
const DEFAULT_HIGHLIGHT_ATTR = 'data-highlighted-default';
const ACTIVE_HIGHLIGHT_ATTR = 'data-highlighted-active';

const getLineNumberFromLineElement = (el: Element): number | undefined => {
  const text = el
    .querySelector<HTMLElement>(LINE_NUMBER_SELECTOR)
    ?.textContent?.trim();
  if (!text) return undefined;
  const num = Number.parseInt(text, 10);
  return Number.isNaN(num) ? undefined : num;
};

export const getLineNumberFromEvent = (
  event: React.MouseEvent<HTMLElement>
): number | undefined => {
  if (!(event.target instanceof Element)) return undefined;
  const lineElement = event.target.closest(LINE_ROOT_SELECTOR);
  return lineElement ? getLineNumberFromLineElement(lineElement) : undefined;
};

export const clearYamlHighlights = (container: HTMLElement): void => {
  if (!container.isConnected) return;

  container.querySelectorAll<HTMLElement>(LINE_ROOT_SELECTOR).forEach((el) => {
    el.removeAttribute(DEFAULT_HIGHLIGHT_ATTR);
    el.removeAttribute(ACTIVE_HIGHLIGHT_ATTR);
  });
};

export const findYamlScroller = (container: HTMLElement): HTMLElement => {
  for (const selector of SCROLLER_SELECTORS) {
    const element = container.querySelector(selector);
    if (element instanceof HTMLElement) {
      return element;
    }
  }
  return container;
};

export const applyYamlHighlights = (
  container: HTMLElement,
  defaultHighlighted: number[],
  activeHighlighted: number[]
): HTMLElement | null => {
  if (!container.isConnected) return null;

  const defaultSet = new Set(defaultHighlighted);
  const activeSet = new Set(activeHighlighted);
  let firstActive: HTMLElement | null = null;
  let firstDefault: HTMLElement | null = null;

  container.querySelectorAll<HTMLElement>(LINE_ROOT_SELECTOR).forEach((el) => {
    const lineNumber = getLineNumberFromLineElement(el);
    if (lineNumber === undefined) return;

    const isDefault = defaultSet.has(lineNumber);
    const isActive = activeSet.has(lineNumber);

    if (isDefault) {
      el.setAttribute(DEFAULT_HIGHLIGHT_ATTR, 'true');
    } else {
      el.removeAttribute(DEFAULT_HIGHLIGHT_ATTR);
    }

    if (isActive) {
      el.setAttribute(ACTIVE_HIGHLIGHT_ATTR, 'true');
    } else {
      el.removeAttribute(ACTIVE_HIGHLIGHT_ATTR);
    }

    if (isActive && !firstActive) firstActive = el;
    if (isDefault && !firstDefault) firstDefault = el;
  });

  return firstActive || firstDefault;
};

const getLineText = (line: HTMLElement): string => {
  const indentation =
    line.querySelector<HTMLElement>(LINE_INDENT_SELECTOR)?.textContent ?? '';
  const content =
    line.querySelector<HTMLElement>(LINE_CONTENT_SELECTOR)?.textContent ?? '';
  return `${indentation}${content}`;
};

export const getHighlightedYamlText = (
  container: HTMLElement,
  type: 'active' | 'default' = 'active'
): string => {
  const attr =
    type === 'active' ? ACTIVE_HIGHLIGHT_ATTR : DEFAULT_HIGHLIGHT_ATTR;
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      `${LINE_ROOT_SELECTOR}[${attr}="true"]`
    )
  )
    .map(getLineText)
    .filter(Boolean)
    .join('\n');
};
